import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { ManualPromptEntry, PhaseView } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

const MAX_CONTENT = 4000;

/**
 * Manual prompt injection for one phase of one strategy.
 *
 * <p>Entries are free text, stored verbatim and rendered into the email
 * generator's system prompt under "HUMAN GUIDANCE (must follow)" — ahead of
 * retrieved knowledge and training notes, so they take precedence. Deactivating
 * an entry withdraws it from the prompt without discarding the wording.
 */
export default function ManualPromptsCard({
  strategyId,
  phases,
  canEdit,
  selectedPhaseId,
}: {
  strategyId: string;
  phases: PhaseView[];
  canEdit: boolean;
  /**
   * Scopes the card to one phase and hides its own phase picker. The strategy
   * editor passes this because the flowchart selection already says which phase
   * the operator is configuring; omitted when the card stands alone and has to
   * offer the choice itself.
   */
  selectedPhaseId?: string;
}) {
  const controlled = selectedPhaseId !== undefined;
  const [ownPhaseId, setOwnPhaseId] = useState<string>("");
  const phaseId = controlled ? selectedPhaseId : ownPhaseId;

  // Follow the strategy selection: whenever the phase list changes, fall back
  // to the first phase rather than holding a stale id from another strategy.
  useEffect(() => {
    if (controlled) return;
    const first = phases[0];
    if (!first) {
      setOwnPhaseId("");
      return;
    }
    if (!phases.some((p) => p.id === ownPhaseId)) setOwnPhaseId(first.id);
  }, [controlled, phases, ownPhaseId]);

  const listQ = useQuery({
    queryKey: ["manual-prompts", strategyId, phaseId],
    enabled: !!phaseId,
    queryFn: () =>
      api.get<ManualPromptEntry[]>(
        `/api/v1/strategies/${strategyId}/manual-prompts?phaseId=${phaseId}`,
      ),
  });

  if (phases.length === 0) {
    return (
      <Card>
        <CardTitle>Manual prompt injection</CardTitle>
        <p className="text-sm text-[var(--fg-muted)]">
          This strategy has no phases yet — add one in the YAML before injecting guidance.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <CardTitle>Manual prompt injection</CardTitle>
          <p className="text-xs text-[var(--fg-muted)] max-w-2xl">
            Guidance the agent must follow when writing emails for this phase. Injected
            immediately — no redeploy. Active entries outrank retrieved knowledge and training
            notes in the prompt.
          </p>
        </div>
        {controlled ? null : (
          <label className="text-sm">
            <div className="text-xs text-[var(--fg-muted)] mb-1">Phase</div>
            <select
              aria-label="Phase"
              value={phaseId}
              onChange={(e) => setOwnPhaseId(e.target.value)}
              className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm"
            >
              {phases.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.slug})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {canEdit && phaseId ? (
        <InjectForm strategyId={strategyId} phaseId={phaseId} />
      ) : !canEdit ? (
        <p className="text-sm text-[var(--fg-muted)]">
          Read-only — only ADMIN or OPERATOR roles can inject prompts.
        </p>
      ) : null}

      <div className="mt-4 space-y-2">
        {listQ.isLoading ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : listQ.error ? (
          <p className="text-sm text-[var(--danger)]">Could not load manual prompts.</p>
        ) : listQ.data && listQ.data.length > 0 ? (
          listQ.data.map((entry) => (
            <PromptRow
              key={entry.id}
              entry={entry}
              strategyId={strategyId}
              canEdit={canEdit}
            />
          ))
        ) : (
          <p className="text-sm text-[var(--fg-muted)]">
            No prompts injected for this phase.
          </p>
        )}
      </div>
    </Card>
  );
}

function InjectForm({ strategyId, phaseId }: { strategyId: string; phaseId: string }) {
  const qc = useQueryClient();
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (text: string) =>
      api.post<ManualPromptEntry>(`/api/v1/strategies/${strategyId}/manual-prompts`, {
        phaseId,
        content: text,
      }),
    onSuccess: () => {
      setContent("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["manual-prompts", strategyId] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? `Injection failed (${e.status})` : "Injection failed."),
  });

  const trimmed = content.trim();
  const submittable = trimmed.length > 0 && trimmed.length <= MAX_CONTENT;

  return (
    <form
      className="grid gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (submittable) create.mutate(trimmed);
      }}
    >
      <label className="text-sm">
        <div className="text-xs text-[var(--fg-muted)] mb-1">New prompt</div>
        <textarea
          aria-label="New prompt"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          placeholder="e.g. Always confirm the timeline before proposing next steps."
          className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      </label>
      <div className="flex items-center justify-end gap-3">
        <span
          className={cn(
            "text-xs",
            trimmed.length > MAX_CONTENT ? "text-[var(--danger)]" : "text-[var(--fg-muted)]",
          )}
        >
          {trimmed.length}/{MAX_CONTENT}
        </span>
        {error ? <span className="text-sm text-[var(--danger)]">{error}</span> : null}
        <button
          type="submit"
          disabled={!submittable || create.isPending}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {create.isPending ? "Injecting…" : "Inject prompt"}
        </button>
      </div>
    </form>
  );
}

function PromptRow({
  entry,
  strategyId,
  canEdit,
}: {
  entry: ManualPromptEntry;
  strategyId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.content);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["manual-prompts", strategyId] });
  const path = `/api/v1/strategies/${strategyId}/manual-prompts/${entry.id}`;

  const save = useMutation({
    mutationFn: (text: string) => api.put<ManualPromptEntry>(path, { content: text }),
    onSuccess: () => {
      setEditing(false);
      invalidate();
    },
  });
  const setActive = useMutation({
    mutationFn: (active: boolean) => api.patch<ManualPromptEntry>(`${path}/active`, { active }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => api.del<void>(path),
    onSuccess: invalidate,
  });

  const trimmed = draft.trim();

  return (
    <div
      className={cn(
        "rounded-md border border-[var(--border)] p-3",
        !entry.active && "opacity-60",
      )}
    >
      {editing ? (
        <div className="grid gap-2">
          <textarea
            aria-label="Edit prompt"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 text-sm"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(entry.content);
                setEditing(false);
              }}
              className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={trimmed.length === 0 || trimmed.length > MAX_CONTENT || save.isPending}
              onClick={() => save.mutate(trimmed)}
              className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="whitespace-pre-wrap text-sm text-zinc-800">{entry.content}</p>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-[var(--fg-muted)]">
              Injected {formatDateTime(entry.createdAt)}
              {entry.active ? "" : " · inactive"}
            </span>
            {canEdit ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDraft(entry.content);
                    setEditing(true);
                  }}
                  className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setActive.mutate(!entry.active)}
                  className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50"
                >
                  {entry.active ? "Deactivate" : "Reactivate"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("Delete this prompt? This cannot be undone.")) remove.mutate();
                  }}
                  className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
